// File: Pages/ProductPage.js
import React, { useEffect, useState } from 'react';
import { getAllProducts } from '../../Api/Products';
import { searchClients } from '../../Api/Clients'
import {
  storeProductsInIndexedDB,
  clearProductsInIndexedDB,
  initDB,
  getGroupedProductsByNamePrefix,
  normalizeBrandKey,
  findProductByName,
} from '../../Utils/IndexedDB';
import { useOrder } from '../../Context/OrderContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { allowedBrandNames } from '../../Utils/allowedBrandNames';
import { CATEGORIES } from 'Utils/CategoriesList';
import { useMemo } from 'react';
import "../../Assets/Styles/ProductPage.css";
import { BulkOrderPanel } from "./BulkOrderPanel";
import {
  parseBulkOrders,
  matchParsedOrders,
} from "../../Utils/BulkOrderParser";


function ProductPage({ resetSignal }) {
  // ======= State Declarations =======
  const [isLoading, setIsLoading] = useState(false);
  const [groupedProducts, setGroupedProducts] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('brands');
  const [allProducts, setAllProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [rawText, setRawText] = useState("");
  const [parsed, setParsed] = useState([]);
  const [manualOverrides, setManualOverrides] = useState({});
  const [fixingLine, setFixingLine] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const [addingToClientIndex, setAddingToClientIndex] = useState(null);
  const [addSearchTerm, setAddSearchTerm] = useState("");

  const [fixingClientIndex, setFixingClientIndex] = useState(null);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [highlightedClientIndex, setHighlightedClientIndex] = useState(-1);
  const [allClients, setAllClients] = useState([]);


  // Add this useEffect to fetch the clients when ProductPage loads
  useEffect(() => {
    async function fetchAllClients() {
      try {
        // Reusing your existing searchClients function
        const data = await searchClients('');
        setAllClients(data.clients || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    }
    fetchAllClients();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  // ======= Context =======
  const { selectedProducts, setSelectedProducts, updateProduct, removeProduct } = useOrder();

  // Map selected products by id for quick access
  const productMap = {};
  selectedProducts.forEach((p) => (productMap[p.id] = p));

  // ======= Helper: Get initial tab from URL or default =======
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    return ['brands', 'categories', 'all'].includes(tab) ? tab : 'brands';
  };

  // ======= Redirect /products to /products?tab=brands if no tab param =======
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (!urlParams.get('tab')) {
      urlParams.set('tab', 'brands');
      navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
    }
  }, [location, navigate]);


  // ======= Text Normalization Helper =======
  function normalizeText(text) {
    return text
      .normalize("NFD")                 // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, "")  // Remove accents
      .replace(/[\(\),]/g, " ")         // Replace parentheses & commas with spaces
      .toLowerCase()
      .trim();
  }

  // ======= Smart initials-aware search helper =======
  const matchesSearch = (productName, search) => {
    if (!search.trim()) return true;

    const normalizedName = normalizeText(productName);
    const words = normalizedName.split(/\s+/);
    const initials = words.map((w) => w[0]).join('');

    const searchParts = normalizeText(search).split(/\s+/);

    return searchParts.every(
      (part) => words.some((word) => word.startsWith(part)) || initials.includes(part)
    );
  };


  // ======= Load products grouped by brand =======
  async function loadGroupedProducts() {
    const grouped = await getGroupedProductsByNamePrefix();
    setGroupedProducts(grouped);
  }

  // ======= Load all products from IndexedDB =======
  async function loadAllProductsFromIndexedDB() {
    const db = await initDB();
    const tx = db.transaction('products', 'readonly');
    const store = tx.objectStore('products');
    const all = await store.getAll();
    setAllProducts(all);
  }

  // ======= Load products grouped by category =======
  async function loadProductsGroupedByCategory() {
    const db = await initDB();
    const tx = db.transaction('products', 'readonly');
    const store = tx.objectStore('products');
    const all = await store.getAll();

    const grouped = {};
    all.forEach((product) => {
      const catId = product.category_id;
      if (!grouped[catId]) grouped[catId] = [];
      grouped[catId].push(product);
    });

    setProductsByCategory(grouped);
  }

  // ======= Cache products to IndexedDB on mount =======
  useEffect(() => {
    async function cacheAllProductsToIndexedDB() {
      setIsLoading(true);

      // Use sessionStorage to cache only per session (clears on tab/browser close)
      const alreadyFetched = sessionStorage.getItem('productsFetched');

      if (alreadyFetched === 'true') {
        console.log('🟢 Products already fetched this session, loading from IndexedDB...');
        await loadGroupedProducts();
        await loadAllProductsFromIndexedDB();
        setIsLoading(false);
        return;
      }

      await clearProductsInIndexedDB();

      let page = 1;
      const perPage = 100;

      try {
        while (true) {
          const { products } = await getAllProducts(page, perPage);
          if (!products || products.length === 0) break;
          await storeProductsInIndexedDB(products);
          console.log(`📦 Stored page ${page} with ${products.length} products`);
          if (products.length < perPage) break;
          page++;
        }

        // Mark as fetched for this session
        sessionStorage.setItem('productsFetched', 'true');

      } catch (error) {
        console.error('❌ Fetch error:', error);
      }

      await loadGroupedProducts();
      await loadAllProductsFromIndexedDB();
      setIsLoading(false);
    }

    cacheAllProductsToIndexedDB();
  }, []);


  // ======= Load products grouped by category after products cached =======
  useEffect(() => {
    if (!isLoading) {
      loadProductsGroupedByCategory();
    }
  }, [isLoading]);

  // ======= Sync activeTab with URL query param =======
  useEffect(() => {
    const newTab = getInitialTab();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.search]);

  // ======= Handle resetSignal prop to reset selections on nav clicks =======
  useEffect(() => {
    if (resetSignal?.reset) {
      if (resetSignal.tab === 'brands') {
        setSelectedGroup(null);
        setSearchTerm('');
      } else if (resetSignal.tab === 'categories') {
        setSelectedCategory(null);
        setSearchTerm('');
      }
      // Optionally reset other states for 'all' tab if needed
    }
  }, [resetSignal]);

  // ======= Reset search term on group or category change =======
  useEffect(() => {
    setSearchTerm('');
  }, [selectedGroup, selectedCategory]);

  // ======= Reset selections on tab change =======
  useEffect(() => {
    if (activeTab === 'categories') {
      setSelectedGroup(null);
    }
    if (activeTab === 'brands') {
      setSelectedCategory(null);
    }
    if (activeTab === 'all') {
      setSelectedCategory(null);
      setSelectedGroup(null);
    }
  }, [activeTab]);

  // ======= Filtered products based on selected brand and search =======
  const filteredProducts = selectedGroup
    ? (groupedProducts[selectedGroup]?.products || []).filter((product) =>
      matchesSearch(product.name, searchTerm)
    )
    : [];

  // ======= Filtered products based on selected category and search =======
  const filteredCategoryProducts = selectedCategory
    ? (productsByCategory[selectedCategory] || []).filter((product) =>
      matchesSearch(product.name, searchTerm)
    )
    : [];

  // ======= Flatten allowed brand products for 'all products' tab =======
  const allowedBrandKeys = useMemo(
    () => allowedBrandNames.map(normalizeBrandKey),
    []
  );

  const allAllowedProducts = useMemo(() => {
    return Object.values(groupedProducts)
      .filter((group) => allowedBrandKeys.includes(normalizeBrandKey(group.displayName)))
      .flatMap((group) => group.products)
      .filter((product) => matchesSearch(product.name, searchTerm))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groupedProducts, allowedBrandKeys, searchTerm]);


  // ======= Pagination logic for 'all' tab =======
  const itemsPerPage = 30;
  const totalPages = Math.ceil(allAllowedProducts.length / itemsPerPage);
  const currentProducts = allAllowedProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ======= Handlers for product selection and quantity =======
  const handleSelect = (product) => {
    selectProduct(product, 1, "add");
  };


  const handleQuantityChange = (id, value) => {
    if (/^\d*([.,]?\d*)?$/.test(value)) {
      updateProduct(id, 'quantity', value);
    }
  };

  const handleBlur = (id, rawValue) => {
    const parsed = parseFloat(rawValue.replace(',', '.'));
    const validValue = !isNaN(parsed) && parsed > 0 ? parsed.toString() : '1';
    updateProduct(id, 'quantity', validValue);
  };

  const adjustQuantity = (id, amount) => {
    const current = productMap[id]?.quantity || '1';
    const parsed = parseInt(current);
    const next = isNaN(parsed) ? 1 : Math.max(1, parsed + amount);
    updateProduct(id, 'quantity', next.toString());
  };

  // ======= Proceed to checkout =======
  const handleProceed = () => {
    if (selectedProducts.length > 0) {
      navigate('/cart');
    }
  };



  /**
   * Selects a product into the order.
   * - Used by normal clicks, bulk parsing, and manual fixes
   *
   * @param product        Full product object from DB
   * @param quantityToSet  Quantity to apply (from bulk / manual / UI)
   * @param mode           "add" → increment existing quantity
   *                       "set" → overwrite quantity (bulk/manual)
   */
  function selectProduct(product, quantityToSet = 1, mode = "add") {
    const cleanPrice = parseFloat(String(product.price_net).replace(',', '.'));
    const cleanTax = parseFloat(String(product.tax).replace(',', '.'));

    // 🔒 Always sanitize quantity
    const qty = Math.max(1, parseInt(quantityToSet) || 1);

    // Basic product validation
    if (
      typeof product.id !== 'number' ||
      typeof product.name !== 'string' ||
      isNaN(cleanTax)
    ) {
      console.warn('❌ Invalid product format:', product);
      return;
    }

    const exists = productMap[product.id];

    setSelectedProducts(prev => {
      // Product already selected
      if (exists) {
        return prev.map(p =>
          p.id === product.id
            ? {
              ...p,
              quantity:
                mode === "set"
                  ? qty                 // bulk / manual overwrite
                  : parseInt(p.quantity) + qty, // UI increment
            }
            : p
        );
      }

      // Product not yet selected
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price_net: cleanPrice,
          quantity_unit: product.quantity_unit,
          tax: cleanTax,
          quantity: qty,
        },
      ];
    });
  }




  // ======= Render product card component =======
  const renderProductCard = (product, selected) => (
    <div
      key={product.id}
      className={`product-card ${selected ? 'selected' : ''}`}
      onClick={() => handleSelect(product)}
    >

      <div className="product-name">
        {product.name}
        {product.quantity_unit && ` (${product.quantity_unit})`}
      </div>
      <div className="quantity-section">
        {selected && (
          <div className="quantity-control" onClick={(e) => e.stopPropagation()}>
            <button className="btn minus" onClick={() => adjustQuantity(product.id, -1)}>-</button>
            <input
              type="number"
              inputMode="decimal"
              className="quantity"
              value={String(selected.quantity ?? '')}
              onChange={(e) => handleQuantityChange(product.id, e.target.value)}
              onBlur={(e) => handleBlur(product.id, e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
              }}
            />

            <button className="btn plus" onClick={() => adjustQuantity(product.id, 1)}>+</button>
          </div>

        )}
      </div>
      <div className='product-body'>
        <div className="product-price">
          {selected && parseInt(selected.quantity) > 1 ? (
            <div>
              <span>{product.price_net}€
              </span>
              <span>{' → '}{(parseFloat(product.price_net) * parseInt(selected.quantity)).toFixed(2)}€</span>
            </div>
          ) : (
            `${product.price_net}€`
          )}
        </div>
        {selected && (
          <div>
            <svg
              onClick={(e) => {
                e.stopPropagation();
                removeProduct(product.id);
              }}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              color="#a2a9ad"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              viewBox="0 0 24 24"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );


  // ==================================================================== //


  // ======= Bulk Logic =======
  async function handleParse() {
    const parsedOrders = parseBulkOrders(rawText);

    const matched = parsedOrders.map(group => {
      // 1. Client Matching
      const cleanName = (group.client || "").replace(/^Cde\s+/i, "").trim().toLowerCase();
      const systemClient = allClients.find(c =>
        c.name?.toLowerCase().includes(cleanName)
      );

      // 2. Product Matching (Pass group.products, fallback to empty array)
      // We wrap this in a try/catch or a check to ensure it returns an array
      let matchedProducts = [];
      try {
        matchedProducts = matchParsedOrders(
          group.products || [],
          allProducts,
          manualOverrides
        ) || []; // Fallback if the function returns null/undefined
      } catch (err) {
        console.error("MatchParsedOrders failed for group:", group.client, err);
        matchedProducts = [];
      }

      return {
        ...group,
        matchedClient: systemClient || null,
        clientMatchStatus: systemClient ? 'exact' : 'none',
        products: matchedProducts // Guaranteed array
      };
    });

    // 3. Automation Loop
    matched.forEach(group => {
      // Check if products exists before forEach
      if (Array.isArray(group.products)) {
        group.products.forEach(p => {
          if (p.matchedProduct && (p.matchedBy === "exact" || p.matchedBy === "manual")) {
            selectProduct(p.matchedProduct, p.quantity, "set");
          }
        });
      }
    });

    setParsed(matched);
  }

  function applyManualClientFix(groupIndex, clientObj) {
    setParsed(prev => {
      const updated = [...prev];
      updated[groupIndex] = { ...updated[groupIndex], matchedClient: clientObj, clientMatchStatus: 'exact' };
      return updated;
    });
    setFixingClientIndex(null);
    setClientSearchQuery("");
  }

  /**
 * Applies a manual product fix for a parsed line.
 * - Saves override (rawName → productId)
 * - Updates parsed preview immediately
 * - Forces product selection with exact quantity
 *
 * This is the bridge between:
 *   bulk parsing  →  real cart state
 */
  function applyManualFix(rawName, product, quantity) {
    // Persist override so future parses auto-match
    setManualOverrides(prev => ({
      ...prev,
      [rawName]: product.id,
    }));

    // Update parsed preview UI
    setParsed(prev =>
      prev.map(order => ({
        ...order,
        products: order.products.map(p =>
          p.name === rawName
            ? {
              ...p,
              matchedProduct: product,
              confidence: 1,
              matchedBy: "manual",
            }
            : p
        ),
      }))
    );

    // 🔥 CRITICAL: force exact quantity from bulk/manual
    selectProduct(product, quantity, "set");

    setFixingLine(null);
    setSearchTerm("");
  }



  // ======= Render function for bulk order products =======
  function getMatchColor(p) {
    if (p.matchedBy === "manual" || p.matchedBy === "exact") return "green";
    if (p.matchedBy === "fuzzy" || p.matchedBy === "partial") return "orange";
    return "red";
  }


  /* =====================================================
     🧹 Delete bulk item (and unselect product if needed)
     ===================================================== */
  function deleteBulkItem(rawName, productId) {
    // 1️⃣ Remove from selected products (cart)
    if (productId) {
      removeProduct(productId);
    }

    // 2️⃣ Remove from parsed bulk list
    setParsed(prev =>
      prev.map(group => ({
        ...group,
        products: group.products.filter(p => p.name !== rawName),
      }))
    );
  }

  /* =====================================================
     🟢 Render CONFIRMED bulk product (green state)
     Uses SAME logic as product card UI
     ===================================================== */
  const renderConfirmedBulkProduct = (p) => {
    const product = p.matchedProduct;
    const selected = productMap[product.id];

    return (
      <div className="bulk-product-confirmed">
        {/* Product name + unit */}
        <div className="product-name">
          {product.name}
          {product.quantity_unit && ` (${product.quantity_unit})`}
        </div>

        {/* Quantity controls */}
        <div className="quantity-section">
          {selected && (
            <div
              className="quantity-control"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="btn minus"
                onClick={() => adjustQuantity(product.id, -1)}
              >
                -
              </button>

              <input
                type="number"
                inputMode="decimal"
                className="quantity"
                value={String(selected.quantity ?? "")}
                onChange={(e) =>
                  handleQuantityChange(product.id, e.target.value)
                }
                onBlur={(e) => handleBlur(product.id, e.target.value)}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key))
                    e.preventDefault();
                }}
              />

              <button
                className="btn plus"
                onClick={() => adjustQuantity(product.id, 1)}
              >
                +
              </button>
            </div>
          )}
        </div>

        {/* Price + delete */}
        <div className="product-body">
          <div className="product-price">
            {selected && parseInt(selected.quantity) > 1 ? (
              <>
                <span>{product.price_net}€</span>
                <span>
                  {" → "}
                  {(product.price_net * selected.quantity).toFixed(2)}€
                </span>
              </>
            ) : (
              `${product.price_net}€`
            )}
          </div>

          {/* Delete button (always visible) */}
          <svg
            onClick={() => deleteBulkItem(p.name, product.id)}
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            style={{ cursor: "pointer" }}
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          </svg>
        </div>
      </div>
    );
  };

  /* =====================================================
     🟠🔴 Render UNCONFIRMED bulk product (raw state)
     Keeps color, Fix button, clickable match
     ===================================================== */
  const renderUnconfirmedBulkProduct = (p) => {
    const color = getMatchColor(p);

    const results =
      fixingLine?.rawName === p.name
        ? allProducts
          .filter(prod =>
            matchesSearch(prod.name, searchTerm || p.name)
          )
          .slice(0, 8)
        : [];

    return (
      <div className="bulk-product-raw">
        {p.name} — qty: {p.quantity}


        {/* MATCH LABEL */}
        {p.matchedProduct ? (
          <span
            style={{
              color,
              cursor: "pointer",
              textDecoration:
                p.matchedBy !== "manual" ? "underline" : "none",
            }}
            onClick={() =>
              applyManualFix(p.name, p.matchedProduct, p.quantity)
            }
          >

            {p.matchedBy === "manual" || p.matchedBy === "exact" ? (
              <>✓ {p.matchedProduct.name}</>
            ) : (
              <>
                <br />
                ⚠️
                {p.matchedProduct.name}
              </>
            )}
          </span>

        ) : (
          <span style={{ color: "red" }}>  ✗ not found</span>
        )}

        {/* ACTIONS */}
        <div className="bulk-actions">
          <button
            onClick={() => {
              setFixingLine({ rawName: p.name, quantity: p.quantity });
              setSearchTerm(p.matchedProduct?.name || p.name);
              setActiveIndex(0);
            }}
          >
            ✎ Fix
          </button>

          <button
            onClick={() =>
              deleteBulkItem(p.name, p.matchedProduct?.id)
            }
          >
            🗑
          </button>
        </div>

        {/* 🔧 INLINE FIX PANEL (THIS WAS MISSING) */}
        {fixingLine?.rawName === p.name && (
          <div className="bulk-fix-panel">
            <input
              autoFocus
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={e => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActiveIndex(i =>
                    Math.min(i + 1, results.length - 1)
                  );
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActiveIndex(i => Math.max(i - 1, 0));
                }
                if (e.key === "Enter" && results[activeIndex]) {
                  e.preventDefault();
                  applyManualFix(
                    p.name,
                    results[activeIndex],
                    p.quantity
                  );
                  setFixingLine(null);
                  setSearchTerm("");
                }
              }}
              placeholder="Search product…"
            />

            <ul>
              {results.map((prod, index) => (
                <li
                  key={prod.id}
                  style={{
                    background:
                      index === activeIndex ? "#eef" : "transparent",
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    applyManualFix(p.name, prod, p.quantity);
                    setFixingLine(null);
                    setSearchTerm("");
                  }}
                >
                  {prod.name}
                </li>
              ))}
            </ul>

            <button
              onClick={() => {
                setFixingLine(null);
                setSearchTerm("");
              }}
            >
              cancel
            </button>
          </div>
        )}
      </div>
    );
  };


  /* =====================================================
     🔀 Main dispatcher
     Decides which UI to render
     ===================================================== */
  const renderProductBulkOrder = (p) => {
    const isConfirmed =
      p.matchedBy === "manual" || p.matchedBy === "exact";

    return isConfirmed
      ? renderConfirmedBulkProduct(p)
      : renderUnconfirmedBulkProduct(p);
  };

  function extractPiecesPerBoxFromName(name = "") {
    const match = name.match(/\bC(\d+)\b/i);
    return match ? parseInt(match[1], 10) : null;
  }

  function resolvePiecesPerBox(parsedItem, matchedProduct) {
    // 1️⃣ Cxx in product name
    const fromName = extractPiecesPerBoxFromName(matchedProduct?.name);
    if (fromName) return fromName;

    // 2️⃣ multiplier unitSize (from 3x1,5 or 2x40)
    if (parsedItem.unitSize) return parsedItem.unitSize;

    // 3️⃣ unknown
    return null;
  }

  function resolveBoxCount(parsedItem, piecesPerBox) {
    if (piecesPerBox && parsedItem.quantity) {
      const calc = parsedItem.quantity / piecesPerBox;
      // Return a float, but we will format it in the UI
      return calc;
    }
    return 0;
  }
  /**
   * Compute logistics-only stats for ONE confirmed bulk product.
   * ❌ No prices
   * ✅ Only quantities & boxes
   */
  function computeBulkProductStats(p) {
    const product = p.matchedProduct;
    if (!product) return null;

    // 🟢 Get live quantity from your OrderContext via productMap
    const selectedItem = productMap[product.id];
    const liveQty = selectedItem ? parseInt(selectedItem.quantity) : p.quantity;

    const piecesPerBox = resolvePiecesPerBox(p, product);

    // 🟢 Pass the liveQty to the box resolver
    const boxCount = resolveBoxCount({ ...p, quantity: liveQty }, piecesPerBox);

    return {
      productId: product.id,
      name: product.name,
      piecesPerBox,
      boxCount,
      totalPieces: liveQty, // This ensures the summary reflects the +/- clicks
    };
  }

  /**
   * Compute summary for ONE CLIENT.
   * Includes:
   *  - Confirmed products in totals
   *  - Not found products listed separately
   */
  function computeClientSummary(clientProducts, selectedProductsMap) {
    // ✅ Confirmed (green) products
    const rows = clientProducts
      .filter(p => p.matchedBy === "manual" || p.matchedBy === "exact")
      .map(p => computeBulkProductStats(p, selectedProductsMap))
      .filter(Boolean);

    // 🔴 Not found products
    const notFound = clientProducts.filter(
      p => p.matchedBy === "none"
    );

    // ✅ Totals only from confirmed
    const totals = rows.reduce(
      (acc, r) => {
        acc.totalBoxes += r.boxCount || 0;
        acc.totalPieces += r.totalPieces || 0;
        return acc;
      },
      { totalBoxes: 0, totalPieces: 0 }
    );

    return {
      rows,       // confirmed products
      notFound,   // unmatched products
      totals
    };
  }
  /**
   * Compute GLOBAL summary across ALL clients.
   * Groups by productId.
   */
  function computeGlobalSummary(parsedOrders) {
    const allConfirmed = parsedOrders.flatMap(order =>
      order.products.filter(
        p => p.matchedBy === "manual" || p.matchedBy === "exact"
      )
    );

    const rows = allConfirmed
      .map(computeBulkProductStats)
      .filter(Boolean);

    const byProduct = {};

    rows.forEach(r => {
      if (!byProduct[r.productId]) {
        byProduct[r.productId] = { ...r };
      } else {
        byProduct[r.productId].boxCount += r.boxCount || 0;
        byProduct[r.productId].totalPieces += r.totalPieces || 0;
      }
    });

    return Object.values(byProduct);
  }

  /* =====================================================
     🧠 MEMOIZED SUMMARIES
     ===================================================== */

  const clientSummaries = useMemo(() => {
    return parsed.map(group => ({
      client: group.client,
      summary: computeClientSummary(group.products),
    }));
  }, [parsed, productMap]); // 👈 Added productMap here

  const globalSummary = useMemo(() => {
    return computeGlobalSummary(parsed);
  }, [parsed, productMap]); // 👈 Added productMap here


  function addProductToClient(clientIndex, product) {
    // 1. Update the Parsed UI state so it shows up under the client
    setParsed(prev => {
      const newParsed = [...prev];
      newParsed[clientIndex].products.push({
        name: product.name,
        quantity: 1,
        matchedProduct: product,
        matchedBy: "manual", // This ensures it gets the green UI and +/- buttons
        confidence: 1
      });
      return newParsed;
    });

    // 2. Add to actual cart
    selectProduct(product, 1, "add");

    // 3. Reset search state
    setAddingToClientIndex(null);
    setAddSearchTerm("");
  }

const copyCleanResult = () => {
  if (parsed.length === 0) return;

  // Build the text string
  const cleanText = parsed.map(group => {
    // Use the matched client name if available, otherwise the raw name
    const clientName = group.matchedClient ? group.matchedClient.name : group.client;
    const header = clientName.startsWith("Cde") ? clientName : `Cde ${clientName}`;

    const lines = group.products.map(p => {
      // Use the matched product name so it matches 100% next time
      const productName = p.matchedProduct ? p.matchedProduct.name : p.name;
      return `- ${productName} ${p.quantity}`;
    });

    return `${header}\n${lines.join('\n')}`;
  }).join('\n\n');

  // Copy to clipboard
  navigator.clipboard.writeText(cleanText).then(() => {
    alert("Clean result copied! You can paste this back later to skip the fixing process.");
  }).catch(err => {
    console.error('Could not copy text: ', err);
  });
};

  // ======= Render Component JSX =======
  return (
    <div className="product-container">
      {/* --- BULK ORDER PANEL --- */}
      <div>
        <BulkOrderPanel
          rawText={rawText}
          onRawTextChange={setRawText}
          onParse={handleParse}
        />

        <h3>Parsed Preview:</h3>
        {/* ===== PER CLIENT ===== */}
        {parsed.map((group, clientIndex) => {
           const { rows, totals, notFound } = computeClientSummary(group.products)
          // Filter results for the dropdown
          const filteredClients = allClients
            .filter(c => c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()))
            .slice(0, 8);

          return (
            <div key={clientIndex} className="bulk-client-block">
              <div className="client-header" style={{
                borderBottom: '2px solid',
                color: group.matchedClient ? '#2e7d32' : '#d32f2f',
                paddingBottom: '8px',
                marginBottom: '10px'
              }}>
                {fixingClientIndex === clientIndex ? (
                  <div className="client-search-inline">
                    <input
                      autoFocus
                      className="bulk-fix-panel"
                      placeholder="Search real client..."
                      value={clientSearchQuery}
                      onChange={(e) => {
                        setClientSearchQuery(e.target.value);
                        setHighlightedClientIndex(0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                          setHighlightedClientIndex(prev => Math.min(prev + 1, filteredClients.length - 1));
                        } else if (e.key === "ArrowUp") {
                          setHighlightedClientIndex(prev => Math.max(prev - 1, 0));
                        } else if (e.key === "Enter" && filteredClients[highlightedClientIndex]) {
                          applyManualClientFix(clientIndex, filteredClients[highlightedClientIndex]);
                        } else if (e.key === "Escape") {
                          setFixingClientIndex(null);
                        }
                      }}
                    />

                    {clientSearchQuery.length > 0 && (
                      <div className="mini-search-results">
                        {filteredClients.map((c, idx) => (
                          <div
                            key={c.id}
                            className={`mini-result-item ${idx === highlightedClientIndex ? 'highlighted' : ''}`}
                            style={{ background: idx === highlightedClientIndex ? '#eef' : 'transparent' }}
                            onMouseEnter={() => setHighlightedClientIndex(idx)}
                            onClick={() => applyManualClientFix(clientIndex, c)}
                          >
                            <strong>{c.name}</strong> <small>({c.email})</small>
                          </div>
                        ))}
                      </div>
                    )}
                    <button className="btn-cancel" onClick={() => setFixingClientIndex(null)}>Cancel</button>
                  </div>
                ) : (
                  <div
                    className="client-title-display"
                    onClick={() => {
                      setFixingClientIndex(clientIndex);
                      setClientSearchQuery(group.client.replace("Cde ", ""));
                    }}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  >
                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {group.matchedClient ? `✅ ${group.matchedClient.name}` : `⚠️ ${group.client}`}
                    </span>
                    {group.matchedClient && (
                      <span style={{ marginLeft: 10, fontSize: '0.8rem', opacity: 0.7 }}>
                        ID: {group.matchedClient.id}
                      </span>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#666' }}> (Click to Edit)</span>
                  </div>
                )}
              </div>

              <div className="product-parsed-list">
                {group.products.map(renderProductBulkOrder)}
              </div>

              {/* CLIENT SUMMARY */}
              {(rows.length > 0 || notFound.length > 0) && (
                <div className="bulk-summary">
                  {/* Matched / Found Rows */}
                  {rows.length > 0 &&
                    rows.map(r => (
                      <div key={r.productId} className="summary-row">
                        <span>{r.name}</span>
                        <span>
                          {r.boxCount > 0
                            ? `Box: ${Number(r.boxCount.toFixed(2))} — Qt: ${r.totalPieces}`
                            : `Qt: ${r.totalPieces}`}
                        </span>
                      </div>
                    ))}

                  {/* Totals for matched items */}
                  {rows.length > 0 && (
                    <div className="summary-total">
                      <span>Boxes: {totals.totalBoxes.toFixed(1)}</span>
                      <span>Units: {totals.totalPieces}</span>
                    </div>
                  )}

                  {/* Not Found Items */}
                  {notFound.length > 0 && (
                    <div className="not-found-summary" style={{ marginTop: "12px", borderTop: "1px solid #ccc", paddingTop: "8px" }}>
                      <h4 style={{ color: "red" }}>❌ Not Found ({notFound.length})</h4>
                      {notFound.map((item, idx) => (
                        <div key={idx} style={{ color: "#b00020" }}>
                          • {item.name} — qty: {item.quantity}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
<div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
  <button 
    className="btn-primary" 
    onClick={copyCleanResult}
    style={{ backgroundColor: '#2e7d32', color: 'white', padding: '10px 20px', borderRadius: '5px' }}
  >
    📋 Copy Clean Result (for later)
  </button>
  
  <button 
    className="btn-secondary" 
    onClick={() => { setParsed([]); setRawText(""); }}
    style={{ padding: '10px 20px', borderRadius: '5px' }}
  >
    Clear All
  </button>
</div>
        {/* ===== GLOBAL SUMMARY ===== */}
        {globalSummary.length > 0 && (
          <div className="bulk-summary global-summary">
            <h3 className="summary-title">All Clients Summary</h3>

            {globalSummary.map(r => {
              // Calculate full boxes and the remaining loose units
              const fullBoxes = Math.floor(r.totalPieces / (r.piecesPerBox || 1));
              const remainder = r.totalPieces % (r.piecesPerBox || 1);

              return (
                <div key={r.productId} className="summary-row">
                  <span className="summary-name">{r.name}</span>

                  <span className="summary-values">
                    {r.piecesPerBox ? (
                      <span>
                        {/* If there are full boxes, show them */}
                        {fullBoxes > 0 && `Box: ${fullBoxes}`}
                        {/* If there is a remainder, show it with a + sign */}
                        {remainder > 0 && ` + ${remainder} pcs`}
                        {/* Always show total Qt for reference */}
                        {` (Total: ${r.totalPieces})`}
                      </span>
                    ) : (
                      <span>Qt: {r.totalPieces}</span>
                    )}
                  </span>
                </div>
              );
            })}

            {/* TOTAL */}
            <div className="summary-row summary-total">
              <span>Grand Total</span>
              <span className="summary-values">
                <span>
                  {(() => {
                    const totalBoxes = globalSummary.reduce((acc, r) => acc + (r.boxCount || 0), 0);
                    const totalUnits = globalSummary.reduce((acc, r) => acc + (r.totalPieces || 0), 0);

                    // Option A: Just round it nicely
                    return `Boxes: ${Math.round(totalBoxes * 100) / 100} | Units: ${totalUnits}`;

                    // Option B (Uncomment if preferred): Separate Full Boxes from loose items
                    // const full = Math.floor(totalBoxes);
                    // return `Full Boxes: ${full} | Total Units: ${totalUnits}`;
                  })()}
                </span>
              </span>
            </div>
          </div>
        )}


      </div>



      {isLoading ? (
        <p>Loading products...</p>
      ) : (
        <>
          {/* --- BRAND SELECTOR --- */}
          {activeTab === 'brands' && !selectedGroup && (
            <div className="group-selector brand-filter">
              {Object.entries(groupedProducts)
                .filter(([_, group]) =>
                  allowedBrandNames.map(normalizeBrandKey).includes(normalizeBrandKey(group.displayName))
                )
                .sort((a, b) => a[1].displayName.localeCompare(b[1].displayName))
                .map(([key, group]) => (
                  <button key={key} onClick={() => setSelectedGroup(key)}>
                    {group.displayName}
                  </button>
                ))}
            </div>
          )}

          {/* --- CATEGORY SELECTOR --- */}
          {activeTab === 'categories' && !selectedCategory && (
            <div className="group-selector category-filter">
              {CATEGORIES.map((category) => (
                <button key={category.id} onClick={() => setSelectedCategory(category.id)}>
                  {category.name}
                </button>
              ))}
            </div>
          )}

          {/* --- SEARCH BAR --- */}
          {((activeTab === 'brands' && selectedGroup) ||
            (activeTab === 'categories' && selectedCategory) ||
            activeTab === 'all') && (
              <div className="search-wrapper">
                <input
                  type="text"
                  placeholder="🔍 Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                    ❌
                  </button>
                )}
              </div>
            )}

          {/* --- PAGINATION --- */}
          {activeTab === 'all' && totalPages > 1 && (
            <div className="pagination">
              {(() => {
                const pageNumbers = [];
                const maxPagesToShow = 5;
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

                if (startPage > 1) {
                  pageNumbers.push(1);
                  if (startPage > 2) pageNumbers.push('...');
                }

                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(i);
                }

                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) pageNumbers.push('...');
                  pageNumbers.push(totalPages);
                }

                return pageNumbers.map((page, index) =>
                  typeof page === 'number' ? (
                    <button
                      key={index}
                      onClick={() => setCurrentPage(page)}
                      className={currentPage === page ? 'active' : ''}
                    >
                      {page}
                    </button>
                  ) : (
                    <span key={index} className="pagination-ellipsis">
                      ...
                    </span>
                  )
                );
              })()}
            </div>
          )}

          {/* --- PRODUCT GRID --- */}
          {activeTab === 'brands' && selectedGroup ? (
            <div className="product-grid">
              {filteredProducts.map((product) => {
                const selected = productMap[product.id];
                return renderProductCard(product, selected);
              })}
            </div>
          ) : activeTab === 'categories' && selectedCategory ? (
            <div className="product-grid">
              {filteredCategoryProducts.map((product) => {
                const selected = productMap[product.id];
                return renderProductCard(product, selected);
              })}
            </div>
          ) : activeTab === 'all' ? (
            <div className="product-grid">
              {currentProducts.map((product) => {
                const selected = productMap[product.id];
                return renderProductCard(product, selected);
              })}
            </div>
          ) : activeTab === 'brands' && !selectedGroup ? (
            <p>Please select a brand above to see products.</p>
          ) : activeTab === 'categories' && !selectedCategory ? (
            <p>Please select a category above to see products.</p>
          ) : null}


          {/* --- PROCEED BUTTON --- */}
          {selectedProducts.length > 0 && (
            <div className="checkout-actions">
              <button className="proceed-btn" onClick={handleProceed}>
                ✅ Proceed to Checkout
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ProductPage;
